import { moneyFormat } from "@redotech/money/util";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import AlarmClockIcon from "@redotech/redo-icons/alarm-clock.svg";
import CopyIcon from "@redotech/redo-icons/copy-03.svg";
import ImageIcon from "@redotech/redo-icons/image-01.svg";
import LayoutGridIcon from "@redotech/redo-icons/layout-grid-01.svg";
import LinkExternalIcon from "@redotech/redo-icons/link-external-02.svg";
import StarIcon from "@redotech/redo-icons/star-01.svg";
import TagIcon from "@redotech/redo-icons/tag-01.svg";
import ShippingTruckIcon from "@redotech/redo-icons/truck-01.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { CheckoutExperienceStep } from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import {
  CheckoutOptimizationWidgetType,
  checkoutOptimizationWidgetTypeToLabel,
} from "@redotech/redo-model/checkouts/widget";
import { canUsePreviewMode } from "@redotech/redo-model/conversion/features";
import {
  CheckoutExperience,
  PREVIEW_CHECKOUT_OUTBOUND_RATES_NAME,
} from "@redotech/redo-model/integration/redo-in-shipping/ris";
import { ShippingRateTable } from "@redotech/redo-model/integration/redo-in-shipping/shipping-rate-table";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/standard-redo-list-items";
import {
  InspectorShippingMethodItem,
  ShippingMethodItemDisplayTableRows,
} from "@redotech/redo-web/checkouts/shipping/shipping-method-item";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { isDef } from "@redotech/util/checks";
import { DeepPartial } from "@redotech/util/type";
import { JSXElementConstructor, memo, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { CheckoutTreeAnalyticsContext } from "../../../../../conversion/context/checkout-tree-analytics-provider";

export const CheckoutExperienceContent = memo(
  function CheckoutExperienceContent({
    nodeId,
    checkoutExperience,
    widgets,
    showName,
    readonly,
  }: {
    nodeId: string;
    checkoutExperience: CheckoutExperience;
    widgets: DeepPartial<CheckoutExperienceStep>["widgets"];
    showName: boolean;
    readonly: boolean;
  }) {
    const teamId = useParams().teamId;
    const team = useRequiredContext(TeamContext);

    const showPreviewButton = useMemo(
      () =>
        canUsePreviewMode({
          checkoutOptimizationSettings: team.settings?.checkoutOptimization,
        }),
      [team.settings?.checkoutOptimization],
    );

    const handleNavigateToCheckoutExperiencePage = useHandler(() => {
      if (!teamId) {
        return;
      }

      const url = `stores/${teamId}/conversion/checkouts/${checkoutExperience._id}`;
      window.open(url, "_blank");
    });

    const handleWidgetClick = useHandler(() => {
      if (!teamId) {
        return;
      }

      const url = `stores/${teamId}/conversion/checkouts/${checkoutExperience._id}/widgets`;
      window.open(url, "_blank");
    });

    const handleCopyPreviewCode = useHandler(async () => {
      const previewCode = `${PREVIEW_CHECKOUT_OUTBOUND_RATES_NAME}-${nodeId}`;
      await navigator.clipboard.writeText(previewCode);
      toast(`Copied preview code "${previewCode}" to clipboard`, {
        variant: "success",
      });
    });

    return (
      <>
        {showName && (
          <Flex align="flex-start" dir="row" gap="md" justify="space-between">
            <Flex dir="column" gap="xs" justify="flex-start">
              <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                Checkout name
              </Text>
              <Text fontSize="xs" fontWeight="medium" textColor="primary">
                {checkoutExperience.name}
              </Text>
            </Flex>
            <RedoButton
              hierarchy="tertiary"
              IconLeading={LinkExternalIcon}
              onClick={handleNavigateToCheckoutExperiencePage}
              size="xs"
            />
          </Flex>
        )}
        {widgets && widgets.length > 0 ? (
          <Flex dir="column" gap="xl" justify="flex-start">
            <Text fontSize="md" fontWeight="semibold">
              Widgets
            </Text>
            <Flex dir="column" gap="md" w="full">
              {widgets.map((widget, index) =>
                widget ? (
                  <WidgetListItem
                    handleWidgetClick={handleWidgetClick}
                    key={index}
                    widgetType={widget}
                  />
                ) : null,
              )}
            </Flex>
          </Flex>
        ) : null}
        <Flex dir="column" gap="xl" justify="flex-start">
          <Flex align="center" dir="row" gap="md" justify="flex-start">
            <Text fontSize="md" fontWeight="semibold">
              Shipping methods
            </Text>
            {showPreviewButton && (
              <Tooltip
                subtitle="Enter the copied code as the first name in the checkout to preview this experience."
                title="Copy preview code"
              >
                <RedoButton
                  hierarchy="tertiary"
                  IconLeading={CopyIcon}
                  onClick={handleCopyPreviewCode}
                  size="xs"
                />
              </Tooltip>
            )}
          </Flex>
          <Flex dir="column" gap="md">
            {checkoutExperience.rateTables.map((rateTable, index) => (
              <InspectorShippingMethodItemWrapper
                key={index}
                nodeId={nodeId}
                rateTable={rateTable}
                readonly={readonly}
                useConstraintsV2={!!checkoutExperience.useConstraintsV2}
              />
            ))}
          </Flex>
        </Flex>
      </>
    );
  },
);

const InspectorShippingMethodItemWrapper = memo(
  function InspectorShippingMethodItemWrapper({
    rateTable,
    nodeId,
    readonly,
    useConstraintsV2,
  }: {
    rateTable: ShippingRateTable;
    nodeId: string;
    readonly: boolean;
    useConstraintsV2: boolean;
  }) {
    const { analyticsForCheckoutNodeByNodeId, checkoutTreeAnalyticsLoad } =
      useContext(CheckoutTreeAnalyticsContext);

    const displayTableRows: ShippingMethodItemDisplayTableRows = useMemo(() => {
      // if in edit mode, don't show metrics
      if (!readonly) {
        return { pending: false, value: [] };
      }

      if (
        checkoutTreeAnalyticsLoad.pending &&
        !analyticsForCheckoutNodeByNodeId[nodeId]
      ) {
        return { pending: true };
      } else if (!analyticsForCheckoutNodeByNodeId[nodeId]) {
        return { pending: false, value: [] };
      } else {
        const rateTableAnalytics =
          analyticsForCheckoutNodeByNodeId[nodeId]?.byRateTableId[
            rateTable._id
          ];
        if (!rateTableAnalytics) {
          return {
            pending: false,
            value: [
              { label: "Orders", value: "-" },
              { label: "Revenue", value: "-" },
              { label: "Shipping revenue", value: "-" },
            ],
          };
        }

        return {
          pending: false,
          value: [
            {
              label: "Orders",
              value: `${rateTableAnalytics.orders.count} (${rateTableAnalytics.orders.relativePercentage.toFixed(1)}%)`,
            },
            {
              label: "Revenue",
              value: moneyFormat(rateTableAnalytics.revenue),
            },
            {
              label: "Shipping revenue",
              value:
                rateTableAnalytics.orders.count > 0
                  ? moneyFormat(rateTableAnalytics.shippingRevenue)
                  : "-",
            },
          ].filter(isDef),
        };
      }
    }, [
      analyticsForCheckoutNodeByNodeId,
      checkoutTreeAnalyticsLoad,
      nodeId,
      rateTable,
      readonly,
    ]);

    return (
      <InspectorShippingMethodItem
        displayTableRows={displayTableRows}
        rateTable={{ ...rateTable, id: rateTable._id }}
        useConstraintsV2={useConstraintsV2}
      />
    );
  },
);

const widgetTypeToIcon: Record<
  CheckoutOptimizationWidgetType,
  JSXElementConstructor<any>
> = {
  [CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF]: AlarmClockIcon,
  [CheckoutOptimizationWidgetType.THRESHOLD_UPSELL]: TagIcon,
  [CheckoutOptimizationWidgetType.LOW_PROFILE_PRODUCT_UPSELL]: TagIcon,
  [CheckoutOptimizationWidgetType.CONTENT_BLOCK]: LayoutGridIcon,
  [CheckoutOptimizationWidgetType.IMAGE_BLOCK]: ImageIcon,
  [CheckoutOptimizationWidgetType.SHIPPING_OPTION_CONTENT]: ShippingTruckIcon,
  [CheckoutOptimizationWidgetType.SHIPPING_OPTION_IMAGE]: ImageIcon,
  [CheckoutOptimizationWidgetType.FAILED_DISCOUNT_RESCUE]: TagIcon,
  [CheckoutOptimizationWidgetType.PRODUCT_REVIEWS]: StarIcon,
};

const WidgetListItem = memo(function WidgetListItem({
  widgetType,
  handleWidgetClick,
}: {
  widgetType: CheckoutOptimizationWidgetType;
  handleWidgetClick: () => void;
}) {
  return (
    <TextRedoListItem
      card
      leadingItem={{ type: "icon", Icon: widgetTypeToIcon[widgetType] }}
      onItemSelected={handleWidgetClick}
      size="sm"
      text={checkoutOptimizationWidgetTypeToLabel[widgetType]}
    />
  );
});
